import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { countries } from './constants';
import { API_URL } from './authService';

const formatDate = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

function Passengers() {
  const [passengers, setPassengers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [passengerToDelete, setPassengerToDelete] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingPassenger, setViewingPassenger] = useState(null);
  const [searchField, setSearchField] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const pageSize = 10;
  const { token } = useAuth();

  const fetchPassengers = useCallback(async (page = 0, sort = '', search = '') => {
    if (!token) return;
    setLoading(true);
    try {
      const sortParam = sort ? `&sort=${sort}` : '';
      const searchParam = search ? `&${search}` : '';
      const response = await fetch(`${API_URL}/api/passengers?page=${page}&size=${pageSize}${sortParam}${searchParam}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Read pagination info from headers
      const totalCount = parseInt(response.headers.get('X-Total-Count') || '0');
      const totalPages = Math.ceil(totalCount / pageSize);
      
      console.log('Response headers:', {
        totalCount: response.headers.get('X-Total-Count'),
        link: response.headers.get('Link')
      });
      
      setPassengers(data);
      setTotalPages(totalPages);
      setTotalElements(totalCount);
      setCurrentPage(page);
      
      console.log('Page:', page, 'Total Elements:', totalCount, 'Total Pages:', totalPages);
    } catch (error) {
      console.error('Error fetching passengers:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPassengers(0, sortField ? `${sortField},${sortDirection}` : '');
  }, [fetchPassengers, sortField, sortDirection]);

  const handlePageChange = (newPage) => {
    setLoading(true);
    const sortParam = sortField ? `${sortField},${sortDirection}` : '';
    const searchParam = buildSearchParam();
    fetchPassengers(newPage, sortParam, searchParam);
  };

  const handleSort = (field) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
    setLoading(true);
    const searchParam = buildSearchParam();
    fetchPassengers(0, `${field},${newDirection}`, searchParam);
  };

  const getSortArrow = (field) => {
    if (sortField !== field) return ' ↕';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const buildSearchParam = () => {
    if (!searchField || !searchValue.trim()) return '';
    
    const fieldMap = {
      'name': 'name.contains',
      'surname': 'surname.contains', 
      'dni': 'dni.equals',
      'phoneNumber': 'phoneNumber.contains',
      'email': 'email.contains'
    };
    
    const apiField = fieldMap[searchField];
    return apiField ? `${apiField}=${encodeURIComponent(searchValue.trim())}` : '';
  };

  const handleSearchFieldChange = (e) => {
    setSearchField(e.target.value);
    setSearchValue('');
    setCurrentPage(0);
    if (!e.target.value) {
      setLoading(true);
      const sortParam = sortField ? `${sortField},${sortDirection}` : '';
      fetchPassengers(0, sortParam);
    }
  };

  const handleSearchValueChange = (e) => {
    setSearchValue(e.target.value);
    setCurrentPage(0);
  };

  useEffect(() => {
    if (searchField && searchValue.trim()) {
      const timeoutId = setTimeout(() => {
        setLoading(true);
        const sortParam = sortField ? `${sortField},${sortDirection}` : '';
        const searchParam = buildSearchParam();
        fetchPassengers(0, sortParam, searchParam);
      }, 300);
      return () => clearTimeout(timeoutId);
    } else if (searchField && !searchValue.trim()) {
      setLoading(true);
      const sortParam = sortField ? `${sortField},${sortDirection}` : '';
      fetchPassengers(0, sortParam);
    }
  }, [searchValue, searchField, sortField, sortDirection]);

  const calculateAge = (birthDate) => {
    if (!birthDate) return 'N/A';
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    dni: '',
    dniExpirationDate: '',
    sex: '',
    email: '',
    passport: '',
    passportExpirationDate: '',
    birthDate: '',
    phoneNumber: '',
    nationality: '',
    visa: false,
    visaExpirationDate: '',
    cuitOrCuil: ''
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'visa') {
      setFormData({
        ...formData,
        visa: checked,
        visaExpirationDate: checked ? formData.visaExpirationDate : ''
      });
    } else {
      setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
    }
  };

  const createPassenger = async (passengerData) => {
    // Convert empty strings to null for API compatibility
    const cleanedData = {
      ...passengerData,
      sex: passengerData.sex === '' ? null : passengerData.sex,
      birthDate: passengerData.birthDate === '' ? null : passengerData.birthDate,
      dniExpirationDate: passengerData.dniExpirationDate === '' ? null : passengerData.dniExpirationDate,
      passportExpirationDate: passengerData.passportExpirationDate === '' ? null : passengerData.passportExpirationDate,
      visa: passengerData.visa || false,
      visaExpirationDate: (passengerData.visa && passengerData.visaExpirationDate !== '') ? passengerData.visaExpirationDate : null
    };
    const response = await fetch(`${API_URL}/api/passengers`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(cleanedData)
    });
    return response.json();
  };

  const updatePassenger = async (id, passengerData) => {
    // Convert empty strings to null for API compatibility
    const cleanedData = {
      ...passengerData,
      sex: passengerData.sex === '' ? null : passengerData.sex,
      birthDate: passengerData.birthDate === '' ? null : passengerData.birthDate,
      dniExpirationDate: passengerData.dniExpirationDate === '' ? null : passengerData.dniExpirationDate,
      passportExpirationDate: passengerData.passportExpirationDate === '' ? null : passengerData.passportExpirationDate,
      visa: passengerData.visa || false,
      visaExpirationDate: (passengerData.visa && passengerData.visaExpirationDate !== '') ? passengerData.visaExpirationDate : null
    };
    const response = await fetch(`${API_URL}/api/passengers/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(cleanedData)
    });
    return response.json();
  };

  const deletePassenger = async (id) => {
    await fetch(`${API_URL}/api/passengers/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/passengers?dni.equals=${formData.dni}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Error al verificar el DNI del pasajero.');
      }

      const existingPassengers = await response.json();

      if (!editingId && existingPassengers.length > 0) {
        alert('Error: Ya existe un pasajero con el mismo DNI.');
        return;
      }
      if (editingId && existingPassengers.length > 0 && existingPassengers[0].id !== editingId) {
        alert(`Error: El DNI ${formData.dni} ya pertenece a otro pasajero (${existingPassengers[0].name} ${existingPassengers[0].surname}).`);
        return;
      }
      // --- End of DNI Check ---

      if (editingId) {
        await updatePassenger(editingId, formData);
      } else {
        await createPassenger(formData);
      }
      resetForm();
      fetchPassengers(0, sortField ? `${sortField},${sortDirection}` : '', buildSearchParam());
    } catch (error) {
      console.error('Error saving passenger:', error);
      alert('Se produjo un error al guardar el pasajero. Verifique los datos e intente nuevamente.');
    }
  };

  const handleEdit = (passenger) => {
    setFormData(passenger);
    setEditingId(passenger.id);
    setShowForm(true);
  };

  const handleView = (passenger) => {
    setViewingPassenger(passenger);
    setShowViewModal(true);
  };

  const handleDelete = (id) => {
    setPassengerToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await deletePassenger(passengerToDelete);
      const sortParam = sortField ? `${sortField},${sortDirection}` : '';
      const searchParam = buildSearchParam();
      fetchPassengers(currentPage, sortParam, searchParam);
    } catch (error) {
      console.error('Error deleting passenger:', error);
    }
    setShowDeleteModal(false);
    setPassengerToDelete(null);
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setPassengerToDelete(null);
  };

  const resetForm = () => {
    setFormData({
      name: '', surname: '', dni: '', dniExpirationDate: '', sex: '',
      email: '', passport: '', passportExpirationDate: '', birthDate: '', phoneNumber: '', nationality: '',
      visa: false, visaExpirationDate: '', cuitOrCuil: ''
    });
    setShowForm(false);
    setEditingId(null);
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Gestión de Pasajeros</h2>
        <button onClick={() => setShowForm(true)} style={styles.addButton}>
          + Agregar Pasajero
        </button>
      </div>

      <div style={styles.searchContainer}>
        <div style={styles.searchField}>
          <label style={styles.searchLabel}>Buscar por:</label>
          <select 
            value={searchField} 
            onChange={handleSearchFieldChange} 
            style={styles.searchSelect}
          >
            <option value="">Seleccionar campo</option>
            <option value="name">Nombre</option>
            <option value="surname">Apellido</option>
            <option value="dni">DNI</option>
            <option value="phoneNumber">Teléfono</option>
            <option value="email">Email</option>
          </select>
        </div>
        {searchField && (
          <div style={styles.searchField}>
            <label style={styles.searchLabel}>Valor:</label>
            <input
              type="text"
              value={searchValue}
              onChange={handleSearchValueChange}
              placeholder={`Buscar por ${searchField === 'name' ? 'nombre' : searchField === 'surname' ? 'apellido' : searchField === 'dni' ? 'DNI' : searchField === 'phoneNumber' ? 'teléfono' : 'email'}...`}
              style={styles.searchInput}
            />
          </div>
        )}
        {(searchField || searchValue) && (
          <button 
            onClick={() => {
              setSearchField('');
              setSearchValue('');
              setCurrentPage(0);
              setLoading(true);
              const sortParam = sortField ? `${sortField},${sortDirection}` : '';
              fetchPassengers(0, sortParam);
            }}
            style={styles.clearButton}
          >
            Limpiar
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} style={styles.form}>
          <h3 style={styles.formTitle}>{editingId ? 'Editar Pasajero' : 'Nuevo Pasajero'}</h3>
          <div style={styles.formGrid}>
            <div style={styles.fieldContainer}>
              <label style={styles.label}>Apellido</label>
              <input name="surname" value={formData.surname} onChange={handleInputChange} style={styles.input} required />
            </div>
            <div style={styles.fieldContainer}>
              <label style={styles.label}>Nombre</label>
              <input name="name" value={formData.name} onChange={handleInputChange} style={styles.input} required />
            </div>
            <div style={styles.fieldContainer}>
              <label style={styles.label}>DNI</label>
              <input name="dni" value={formData.dni} onChange={handleInputChange} style={styles.input} required />
            </div>
            <div style={styles.fieldContainer}>
              <label style={styles.label}>Vencimiento DNI</label>
              <input name="dniExpirationDate" type="date" value={formData.dniExpirationDate} onChange={handleInputChange} style={styles.input} />
            </div>
            <div style={styles.fieldContainer}>
              <label style={styles.label}>CUIT/CUIL</label>
              <input name="cuitOrCuil" value={formData.cuitOrCuil || ''} onChange={handleInputChange} style={styles.input} />
            </div>
            <div style={styles.fieldContainer}>
              <label style={styles.label}>Fecha de Nacimiento</label>
              <input name="birthDate" type="date" value={formData.birthDate} onChange={handleInputChange} style={styles.input} />
            </div>
            <div style={styles.fieldContainer}>
              <label style={styles.label}>Email</label>
              <input name="email" type="email" value={formData.email} onChange={handleInputChange} style={styles.input} />
            </div>
            <div style={styles.fieldContainer}>
              <label style={styles.label}>Pasaporte</label>
              <input name="passport" value={formData.passport} onChange={handleInputChange} style={styles.input} />
            </div>
            <div style={styles.fieldContainer}>
              <label style={styles.label}>Vencimiento Pasaporte</label>
              <input name="passportExpirationDate" type="date" value={formData.passportExpirationDate || ''} onChange={handleInputChange} style={styles.input} />
            </div>
            <div style={styles.fieldContainer}>
              <label style={styles.label}>Sexo</label>
              <select name="sex" value={formData.sex} onChange={handleInputChange} style={styles.select}>
                <option value="">Seleccionar Sexo</option>
                <option value="MALE">Masculino</option>
                <option value="FEMALE">Femenino</option>
              </select>
            </div>
            <div style={styles.fieldContainer}>
              <label style={styles.label}>Teléfono</label>
              <input name="phoneNumber" value={formData.phoneNumber} onChange={handleInputChange} style={styles.input} />
            </div>
            <div style={styles.fieldContainer}>
              <label style={styles.label}>Nacionalidad</label>
              <select name="nationality" value={formData.nationality} onChange={handleInputChange} style={styles.select}>
                <option value="">Seleccionar Nacionalidad</option>
                {countries.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
            <div style={styles.fieldContainer}>
               <div style={styles.checkboxContainer}>
                  <label style={styles.checkboxLabel}>
                    <input name="visa" type="checkbox" checked={formData.visa || false} onChange={handleInputChange} style={styles.checkbox} />
                    Visa
                  </label>
               </div>
            </div>
            <div style={styles.fieldContainer}>
              <label style={styles.label}>Vencimiento Visa</label>
              <input 
                name="visaExpirationDate" 
                type="date" 
                value={formData.visaExpirationDate || ''} 
                onChange={handleInputChange} 
                style={styles.input} 
                disabled={!formData.visa}
              />
            </div>

          </div>
          <div style={styles.formButtons}>
            <button type="button" onClick={resetForm} style={styles.cancelButton}>Cancelar</button>
            <button type="submit" style={styles.saveButton}>Guardar</button>
          </div>
        </form>
      )}

      <div style={styles.tableContainer}>
        {loading ? (
          <div style={styles.emptyState}>Cargando pasajeros...</div>
        ) : passengers.length === 0 ? (
          <div style={styles.emptyState}>
            No hay pasajeros registrados. ¡Agrega el primero!
          </div>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.sortableHeader} onClick={() => handleSort('surname')}>Apellido y Nombre{getSortArrow('surname')}</th>
                  <th style={styles.sortableHeader} onClick={() => handleSort('dni')}>DNI{getSortArrow('dni')}</th>
                  <th style={styles.sortableHeader} onClick={() => handleSort('birthDate')}>Edad{getSortArrow('birthDate')}</th>
                  <th style={styles.sortableHeader} onClick={() => handleSort('email')}>Email{getSortArrow('email')}</th>
                  <th style={styles.sortableHeader} onClick={() => handleSort('phoneNumber')}>Teléfono{getSortArrow('phoneNumber')}</th>

                  <th style={styles.tableHeader}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {passengers.map(passenger => (
                  <tr key={passenger.id}>
                    <td style={styles.tableCell}>{passenger.surname} {passenger.name}</td>
                    <td style={styles.tableCell}>{passenger.dni}</td>
                    <td style={styles.tableCell}>{calculateAge(passenger.birthDate) === 'N/A' ? 'N/A' : `${calculateAge(passenger.birthDate)} años`}</td>
                    <td style={styles.tableCell}>{passenger.email}</td>
                    <td style={styles.tableCell}>{passenger.phoneNumber}</td>

                    <td style={styles.tableCell}>
                      <div style={styles.actionButtons}>
                        <button onClick={() => handleView(passenger)} style={styles.viewButton}>Ver</button>
                        <button onClick={() => handleEdit(passenger)} style={styles.editButton}>Editar</button>
                        <button onClick={() => handleDelete(passenger.id)} style={styles.deleteButton}>Eliminar</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={styles.pagination}>
              <div style={styles.paginationInfo}>
                Mostrando {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, totalElements)} de {totalElements} pasajeros
              </div>
              <div style={styles.paginationControls}>
                <button 
                  onClick={() => handlePageChange(currentPage - 1)} 
                  disabled={currentPage === 0}
                  style={currentPage === 0 ? styles.paginationButtonDisabled : styles.paginationButton}
                >
                  Anterior
                </button>
                <span style={styles.pageInfo}>Página {currentPage + 1} de {totalPages}</span>
                <button 
                  onClick={() => handlePageChange(currentPage + 1)} 
                  disabled={currentPage >= totalPages - 1}
                  style={currentPage >= totalPages - 1 ? styles.paginationButtonDisabled : styles.paginationButton}
                >
                  Siguiente
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {showViewModal && viewingPassenger && (
        <div style={styles.modalOverlay}>
          <div style={styles.viewModal}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Información del Pasajero</h3>
              <button onClick={() => setShowViewModal(false)} style={styles.closeButton} type="button">×</button>
            </div>
            <div style={styles.viewContent}>
              <div style={styles.passengerInfoSection}>
                <h4 style={styles.passengerInfoTitle}>Datos Personales</h4>
                <div style={styles.viewGrid}>
                  <div style={styles.viewRow}><strong>Apellido:</strong> {viewingPassenger.surname}</div>
                  <div style={styles.viewRow}><strong>Nombre:</strong> {viewingPassenger.name}</div>
                  <div style={styles.viewRow}><strong>DNI:</strong> {viewingPassenger.dni}</div>
                  <div style={styles.viewRow}><strong>Vencimiento DNI:</strong> {formatDate(viewingPassenger.dniExpirationDate)}</div>
                  <div style={styles.viewRow}><strong>CUIT/CUIL:</strong> {viewingPassenger.cuitOrCuil || 'No especificado'}</div>
                  <div style={styles.viewRow}><strong>Email:</strong> {viewingPassenger.email}</div>
                  <div style={styles.viewRow}><strong>Teléfono:</strong> {viewingPassenger.phoneNumber}</div>
                  <div style={styles.viewRow}><strong>Sexo:</strong> {viewingPassenger.sex === 'MALE' ? 'Masculino' : viewingPassenger.sex === 'FEMALE' ? 'Femenino' : 'No especificado'}</div>
                  <div style={styles.viewRow}><strong>Fecha de Nacimiento:</strong> {formatDate(viewingPassenger.birthDate)}</div>
                  <div style={styles.viewRow}><strong>Edad:</strong> {calculateAge(viewingPassenger.birthDate) === 'N/A' ? 'N/A' : `${calculateAge(viewingPassenger.birthDate)} años`}</div>
                  <div style={styles.viewRow}><strong>Nacionalidad:</strong> {viewingPassenger.nationality}</div>
                </div>
              </div>
              <div style={styles.passengerInfoSection}>
                <h4 style={styles.passengerInfoTitle}>Documentación de Viaje</h4>
                <div style={styles.viewGrid}>
                  <div style={styles.viewRow}><strong>Pasaporte:</strong> {viewingPassenger.passport || 'No especificado'}</div>
                  <div style={styles.viewRow}><strong>Vencimiento Pasaporte:</strong> {viewingPassenger.passportExpirationDate ? formatDate(viewingPassenger.passportExpirationDate) : 'No especificado'}</div>
                  <div style={styles.viewRow}><strong>Visa:</strong> {viewingPassenger.visa ? 'Sí' : 'No'}</div>
                  <div style={styles.viewRow}><strong>Vencimiento Visa:</strong> {viewingPassenger.visaExpirationDate ? formatDate(viewingPassenger.visaExpirationDate) : 'No especificado'}</div>
                </div>
              </div>
            </div>
            <div style={styles.modalButtons}>
              <button onClick={() => setShowViewModal(false)} style={styles.modalCancelButton}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Confirmar Eliminación</h3>
            <p style={styles.modalText}>¿Estás seguro de que deseas eliminar este pasajero?</p>
            <div style={styles.modalButtons}>
              <button onClick={cancelDelete} style={styles.modalCancelButton}>Cancelar</button>
              <button onClick={confirmDelete} style={styles.modalConfirmButton}>Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '5px', maxWidth: '100%', margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' },
  title: { fontSize: '36px', fontWeight: 'bold', color: '#333', margin: 0 },
  addButton: { backgroundColor: '#ff9f43', color: 'white', padding: '16px 32px', border: 'none', borderRadius: '8px', fontSize: '20px', fontWeight: '500', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' },
  form: { backgroundColor: 'white', padding: '30px', borderRadius: '12px', marginBottom: '30px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', border: '1px solid #e9ecef' },
  formTitle: { fontSize: '28px', fontWeight: '600', marginBottom: '20px', color: '#333' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', marginBottom: '25px' },
  fieldContainer: { display: 'flex', flexDirection: 'column' },
  label: { fontSize: '16px', fontWeight: 'bold', color: '#495057', marginBottom: '4px' },
  input: { padding: '16px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '18px', backgroundColor: '#fff' },
  select: { padding: '16px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '18px', backgroundColor: '#fff' },
  formButtons: { display: 'flex', gap: '12px', justifyContent: 'flex-end' },
  saveButton: { backgroundColor: '#28a745', color: 'white', padding: '16px 32px', border: 'none', borderRadius: '6px', fontSize: '18px', fontWeight: '500', cursor: 'pointer' },
  cancelButton: { backgroundColor: '#6c757d', color: 'white', padding: '16px 32px', border: 'none', borderRadius: '6px', fontSize: '18px', fontWeight: '500', cursor: 'pointer' },
  tableContainer: { backgroundColor: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', border: '1px solid #e9ecef' },
  table: { width: '100%', borderCollapse: 'collapse' },
  tableHeader: { backgroundColor: '#f8f9fa', fontWeight: '600', padding: '20px 40px', textAlign: 'left', borderBottom: '2px solid #dee2e6', color: '#495057', width: '180px', minWidth: '180px', fontSize: '18px' },
  sortableHeader: { backgroundColor: '#f8f9fa', fontWeight: '600', padding: '20px 40px', textAlign: 'left', borderBottom: '2px solid #dee2e6', color: '#495057', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', fontSize: '18px' },
  tableCell: { padding: '20px 40px', borderBottom: '1px solid #dee2e6', fontSize: '18px' },
  actionButtons: { display: 'flex', gap: '8px', whiteSpace: 'nowrap' },
  editButton: { backgroundColor: '#17a2b8', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '4px', fontSize: '16px', cursor: 'pointer' },
  deleteButton: { backgroundColor: '#dc3545', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '4px', fontSize: '16px', cursor: 'pointer' },
  emptyState: { textAlign: 'center', padding: '40px', color: '#6c757d', fontSize: '20px' },
  pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', borderTop: '1px solid #dee2e6', backgroundColor: '#f8f9fa' },
  paginationInfo: { fontSize: '18px', color: '#6c757d' },
  paginationControls: { display: 'flex', alignItems: 'center', gap: '15px' },
  paginationButton: { padding: '12px 24px', backgroundColor: '#ff9f43', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '18px' },
  paginationButtonDisabled: { padding: '12px 24px', backgroundColor: '#e9ecef', color: '#6c757d', border: 'none', borderRadius: '4px', cursor: 'not-allowed', fontSize: '18px' },
  pageInfo: { fontSize: '18px', fontWeight: '500', color: '#495057' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
  modal: { backgroundColor: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', maxWidth: '400px', width: '90%' },
  modalTitle: { fontSize: '28px', fontWeight: '600', marginBottom: '15px', color: '#333', textAlign: 'center' },
  modalText: { fontSize: '20px', color: '#495057', marginBottom: '25px', textAlign: 'center', lineHeight: '1.5' },
  modalButtons: { display: 'flex', gap: '12px', justifyContent: 'center' },
  modalCancelButton: { backgroundColor: '#6c757d', color: 'white', padding: '16px 32px', border: 'none', borderRadius: '6px', fontSize: '18px', fontWeight: '500', cursor: 'pointer' },
  modalConfirmButton: { backgroundColor: '#dc3545', color: 'white', padding: '16px 32px', border: 'none', borderRadius: '6px', fontSize: '18px', fontWeight: '500', cursor: 'pointer' },
  viewButton: { backgroundColor: '#28a745', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '4px', fontSize: '16px', cursor: 'pointer' },
  viewModal: { backgroundColor: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', maxWidth: '800px', width: '90%', maxHeight: '80vh', overflowY: 'auto' },
  viewContent: { marginBottom: '25px' },
  viewRow: { padding: '12px 0', borderBottom: '1px solid #e9ecef', fontSize: '20px' },
  searchContainer: { backgroundColor: 'white', padding: '20px', borderRadius: '12px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', display: 'flex', gap: '15px', alignItems: 'end', flexWrap: 'wrap' },
  searchField: { display: 'flex', flexDirection: 'column', minWidth: '200px' },
  searchLabel: { fontSize: '14px', fontWeight: '600', color: '#495057', marginBottom: '5px' },
  searchSelect: { padding: '12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '16px', backgroundColor: '#fff' },
  searchInput: { padding: '12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '16px', backgroundColor: '#fff', minWidth: '250px' },
  clearButton: { backgroundColor: '#6c757d', color: 'white', padding: '12px 20px', border: 'none', borderRadius: '6px', fontSize: '16px', cursor: 'pointer', height: 'fit-content' },
  checkboxContainer: { display: 'flex', alignItems: 'center', padding: '12px 0', height: '100%' },
  checkboxLabel: { display: 'flex', alignItems: 'center', fontSize: '18px', color: '#495057', cursor: 'pointer' },
  checkbox: { marginRight: '8px' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  closeButton: { backgroundColor: 'transparent', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6c757d', padding: '0', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' },
  viewGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' },
  passengerInfoSection: { marginBottom: '25px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' },
  passengerInfoTitle: { fontSize: '22px', fontWeight: '600', color: '#495057', marginBottom: '10px', borderBottom: '2px solid #ff9f43', paddingBottom: '5px' },
}

export default Passengers;